import { useGSAP } from "@gsap/react"
import gsap from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import { type RefObject, useRef } from "react"
import { MutuPrompt } from "@/components/ui/mutu-prompt"
import { SectionTitle } from "@/components/ui/section-title"
import { TransmissionPanel } from "@/components/ui/transmission-panel"

gsap.registerPlugin(ScrollTrigger, useGSAP)

const ABOUT_START_MS = 200
const EKLO_START_MS = 450

export function MutuTransmission() {
  const sectionRef = useRef<HTMLElement>(null)
  const bubbleRef = useRef<HTMLDivElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)
  const sweepRef = useRef<HTMLDivElement>(null)

  useGSAP(
    () => {
      if (!sectionRef.current) return

      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        gsap.set([bubbleRef.current, bodyRef.current?.children ?? null], {
          clearProps: "all",
        })
        return
      }

      const setInitial = () => {
        gsap.set(bubbleRef.current, {
          opacity: 0,
          y: 28,
          scale: 0.98,
          transformOrigin: "top right",
        })
        gsap.set(sweepRef.current, { xPercent: -130, opacity: 0 })
        if (bodyRef.current) {
          gsap.set(bodyRef.current.children, { opacity: 0, y: 10 })
        }
      }

      const buildTimeline = (positions: {
        bubbleIn: number
        sweep: number
        sweepOut: number
        body: number
      }) => {
        const tl = gsap.timeline()
        tl.to(
          bubbleRef.current,
          { opacity: 1, y: 0, scale: 1, duration: 0.3, ease: "power3.out" },
          positions.bubbleIn
        )
          .to(
            sweepRef.current,
            {
              xPercent: 130,
              opacity: 0.7,
              duration: 0.3,
              ease: "power2.inOut",
            },
            positions.sweep
          )
          .to(
            sweepRef.current,
            { opacity: 0, duration: 0.1 },
            positions.sweepOut
          )
        if (bodyRef.current) {
          tl.to(
            bodyRef.current.children,
            {
              opacity: 1,
              y: 0,
              duration: 0.35,
              stagger: 0.06,
              ease: "power2.out",
            },
            positions.body
          )
        }
        return tl
      }

      const mm = gsap.matchMedia()

      mm.add("(min-width: 768px)", () => {
        setInitial()
        const tl = buildTimeline({
          bubbleIn: 0.3,
          sweep: 0.35,
          sweepOut: 0.7,
          body: 0.45,
        })
        ScrollTrigger.create({
          trigger: sectionRef.current,
          start: "top top",
          end: "+=140%",
          pin: true,
          scrub: 1,
          anticipatePin: 1,
          invalidateOnRefresh: true,
          animation: tl,
        })
      })

      mm.add("(max-width: 767px)", () => {
        setInitial()
        const HEADING_END = 0.85
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 65%",
            once: true,
          },
        })
        tl.to(
          bubbleRef.current,
          { opacity: 1, y: 0, scale: 1, duration: 0.75, ease: "power3.out" },
          HEADING_END + 0.4
        )
          .to(
            sweepRef.current,
            {
              xPercent: 130,
              opacity: 0.7,
              duration: 0.75,
              ease: "power2.inOut",
            },
            HEADING_END + 0.45
          )
          .to(
            sweepRef.current,
            { opacity: 0, duration: 0.25 },
            HEADING_END + 1.15
          )
        if (bodyRef.current) {
          tl.to(
            bodyRef.current.children,
            {
              opacity: 1,
              y: 0,
              duration: 0.6,
              stagger: 0.16,
              ease: "power2.out",
            },
            HEADING_END + 0.7
          )
        }
      })
    },
    { scope: sectionRef }
  )

  return (
    <section
      ref={sectionRef}
      id="about"
      className="relative z-10 flex min-h-svh w-full flex-col py-16 sm:py-28 md:z-auto lg:py-32"
    >
      <div className="relative z-30 w-full max-w-4xl px-5 sm:pr-10 sm:pl-20 lg:pl-32">
        <div className="flex max-w-2xl justify-center sm:ml-16 lg:ml-28">
          <SectionTitle
            prefix="About"
            accent="Eklo-Verse"
            prefixStartMs={ABOUT_START_MS}
            accentStartMs={EKLO_START_MS}
          />
        </div>

        <SynopsisBubble
          bubbleRef={bubbleRef}
          bodyRef={bodyRef}
          sweepRef={sweepRef}
        />
      </div>
    </section>
  )
}

function SynopsisBubble({
  bubbleRef,
  bodyRef,
  sweepRef,
}: {
  bubbleRef: RefObject<HTMLDivElement | null>
  bodyRef: RefObject<HTMLDivElement | null>
  sweepRef: RefObject<HTMLDivElement | null>
}) {
  return (
    <div
      ref={bubbleRef}
      className="relative mt-8 max-w-2xl sm:mt-10 sm:ml-16 lg:ml-28"
    >
      <TransmissionPanel className="relative overflow-hidden">
        <div
          ref={sweepRef}
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 left-0 w-[45%] bg-linear-to-r from-transparent via-brand/20 to-transparent"
        />

        <div ref={bodyRef} className="relative">
          <MutuPrompt verb="describe" subject="Eklo Verse" />

          <p className="mt-5 font-mono text-foreground text-sm leading-relaxed sm:text-base sm:leading-[1.7]">
            In 2090, Earth is a wasteland after decades of war and a zombie
            outbreak that decimated humanity. Two astronauts return from space
            to find their home unrecognizable, its last flicker of life hidden
            in the remote mountains of Nepal. There, under the shadow of a
            brutal king, they meet the final known human survivor, a mysterious
            figure who may hold the key to the planet&rsquo;s future.
          </p>

          <p className="mt-4 font-mono text-foreground text-sm leading-relaxed sm:text-base sm:leading-[1.7]">
            As they uncover the truth behind this post-apocalyptic world, an
            ancient guardian watches from the shadows, manipulating events to
            fulfill a dark prophecy. With danger closing in from the undead and
            power-hungry factions, the astronauts must navigate a deadly
            struggle for survival, where their arrival could either save what is
            left of humanity or doom it forever.
          </p>

          <p className="mt-5 font-mono text-[10px] text-label uppercase tracking-[0.4em]">
            <span className="text-label-dim">{"//"}</span> end transmission
          </p>
        </div>
      </TransmissionPanel>

      <span
        aria-hidden="true"
        className="absolute top-[28%] right-0 hidden size-4 translate-x-1/2 -translate-y-1/2 rotate-45 border-white/15 border-t border-r bg-background/45 backdrop-blur-sm md:block"
      />
    </div>
  )
}
